import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import camelcaseKeys from "camelcase-keys";
import { AxiosError, AxiosResponse } from "axios";

import { AXIOS } from "../axios";
import { useAuth } from "../../components/auth/AuthProvider";
import { useError } from "../../components/status/ErrorProvider";
import { useSuccess } from "../../components/status/SuccessProvider";
import { MetaSchema } from "../pagination";

export interface RequestSchema {
  id: string;
  uri: string;
  status: "pending" | "approved" | "rejected";
  subject: string;
  key: { id: string; name: string };
  project: { id: string; name: string };
  approver: string;
  requester: string;
  updatedAt: Date;
  message?: string;
  comment?: string;
}

export const useProxyRequests = (body: { status?: "pending" | "closed" }) => {
  const url = useAuth().user.activeOrganization.url;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    { data: RequestSchema[]; meta: MetaSchema }
  >({
    queryKey: [`PROXY REQUESTS ${JSON.stringify(body)}`],
    queryFn: () => AXIOS.post(`${url}/requests`, body),
    retry: 2,
    select: (data) => camelcaseKeys(data.data, { deep: true }),
    staleTime: 1000 * 60 * 2,
    refetchOnMount: false,
  });
};

export const useProxyRequestDetail = (body: { id: string }) => {
  const url = useAuth().user.activeOrganization.url;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    { data: RequestSchema }
  >({
    queryKey: [`PROXY REQUESTS DETAIL ${JSON.stringify(body)}`],
    queryFn: () => AXIOS.post(`${url}/requests/detail`, body),
    retry: 2,
    select: (data) => camelcaseKeys(data.data, { deep: true }),
  });
};

// export const useProxyRequestWrappingKey = () => {
//   const queryClient = useQueryClient();
//   const { setError } = useError();
//   const { setSuccess } = useSuccess();

//   const mutation = useMutation<
//     AxiosResponse<any, any>,
//     AxiosError<{ error: string; message: string }>,
//     KeyRequestSchema
//   >({
//     mutationKey: [`PROXY KEYS REQUEST KEY WRAPPING`],
//     mutationFn: (props: KeyRequestSchema) =>
//       AXIOS.post(`${baseUrl}/request/key/wrapping`, props),
//     onError: (error) => {
//       const data = error.response?.data;
//       const message = data?.error ?? data?.message ?? "please try again";
//       setError({ message, reset: mutation.reset });
//     },
//     onSuccess: (data) => {
//       setSuccess(`Key requested`);

//       queryClient.invalidateQueries({
//         predicate: (query) =>
//           // @ts-ignore
//           query.queryKey[0]?.startsWith("PROXY KEYS"),
//       });
//     },
//   });

//   return mutation;
// };

// type WrappedKeyTaskSchema = {
//   projectId: string;
//   description: string;
//   outputKeyAlgo: OutputKeyAlgo;
//   outputKeyCheckValue: string;
//   wrappingAlgo?: KeyWrapAlgorithm;
//   wrappingKeyId?: string;
// };

// export const useProxyRequestWrappedKeyTask = () => {
//   const queryClient = useQueryClient();
//   const { setError } = useError();
//   const { setSuccess } = useSuccess();

//   const mutation = useMutation<
//     AxiosResponse<any, any>,
//     AxiosError<{ error: string; message: string }>,
//     WrappedKeyTaskSchema
//   >({
//     mutationKey: [`PROXY KEYS REQUEST KEY TASK WRAPPED`],
//     mutationFn: (props: WrappedKeyTaskSchema) =>
//       AXIOS.post(`${baseUrl}/create/key/task/wrapped`, props),
//     onError: (error) => {
//       const data = error.response?.data;
//       const message = data?.error ?? data?.message ?? "please try again";
//       setError({ message, reset: mutation.reset });
//     },
//     onSuccess: (data) => {
//       setSuccess(`Key task created`);

//       queryClient.invalidateQueries({
//         predicate: (query) =>
//           // @ts-ignore
//           query.queryKey[0]?.startsWith("PROXY KEYS"),
//       });
//     },
//   });

//   return mutation;
// };

// type ComponentKeyTaskSchema = {
//   projectId: string;
//   description: string;
//   outputKeyAlgo: OutputKeyAlgo;
//   outputKeyCheckValue: string;
//   compositeTotal: string;
// };

// export const useProxyRequestComponentKeyTask = () => {
//   const queryClient = useQueryClient();
//   const { setError } = useError();
//   const { setSuccess } = useSuccess();

//   const mutation = useMutation<
//     AxiosResponse<any, any>,
//     AxiosError<{ error: string; message: string }>,
//     ComponentKeyTaskSchema
//   >({
//     mutationKey: [`PROXY KEYS REQUEST KEY TASK COMPONENT`],
//     mutationFn: (props: ComponentKeyTaskSchema) =>
//       AXIOS.post(`${baseUrl}/create/key/task/component`, props),
//     onError: (error) => {
//       const data = error.response?.data;
//       const message = data?.error ?? data?.message ?? "please try again";
//       setError({ message, reset: mutation.reset });
//     },
//     onSuccess: (data) => {
//       setSuccess(`Key task created`);

//       queryClient.invalidateQueries({
//         predicate: (query) =>
//           // @ts-ignore
//           query.queryKey[0]?.startsWith("PROXY KEYS"),
//       });
//     },
//   });

//   return mutation;
// };

type RequestPostSchema = {
  id: string;
  comment: string;
};

export type AxiosMutationRequest = UseMutationResult<
  AxiosResponse<any, any>,
  AxiosError<{ message: string }, any>,
  RequestPostSchema,
  unknown
>;

export const useProxyApprove = () => {
  const url = useAuth().user.activeOrganization.url;

  const qc = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }, any>,
    RequestPostSchema
  >({
    mutationKey: [`PROXY REQUEST APPROVE`],
    mutationFn: (props: RequestPostSchema) =>
      AXIOS.post(`${url}/requests/approve`, props),
    onSuccess: async (data) => {
      setSuccess(`Request approved`);

      // refetch all active queries to ensure latest state
      qc.refetchQueries({ type: "active" });

      return camelcaseKeys(data.data, { deep: true });
    },
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};

export const useProxyReject = () => {
  const url = useAuth().user.activeOrganization.url;

  const qc = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }, any>,
    RequestPostSchema
  >({
    mutationKey: [`PROXY REQUEST REJECT`],
    mutationFn: (props: RequestPostSchema) =>
      AXIOS.post(`${url}/requests/reject`, props),
    onSuccess: (data) => {
      setSuccess(`Request rejected`);

      // refetch all active queries to ensure latest state
      qc.refetchQueries({ type: "active" });

      return camelcaseKeys(data.data, { deep: true });
    },
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
  });

  return mutation;
};
